/* eslint-disable no-unused-vars */
/* eslint-disable no-unreachable */

import React, { useContext, createContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes, { oneOfType } from 'prop-types';
import { useFetch } from 'use-http';
import { useParams } from 'react-router';
import { useBee } from './Bee';
import { debounce } from 'lodash';

const servizioContext = createContext();

export function useServizio()
{
   return useContext(servizioContext);
}

export function ServizioCore({ children, ...otherProps })
{

   const {
      idContratto,
      idServizio,
      idGiorno,
      idTurno,
   } = useParams();

   const {
      forceReload: originalForceReload,
      reload,
   } = useBee();

   const forceReload = useMemo(
      () => debounce(originalForceReload, 2000),
      [originalForceReload]
   );

   //inizializzo l'oggetto per la chiamata fetch
   const fetcher = useFetch(process.env.REACT_APP_BASE_API_URL, { cachePolicy: 'no-cache' });

   const [servizio, setServizio] = useState(null);
   const [giorno, setGiorno] = useState(null);
   const [turni, setTurni] = useState(null);
   const [turno, setTurno] = useState(null);

   const [unassigningIds, setUnassigningIds] = useState({});
   const [assigningIds, setAssigningIds] = useState({});
   const [confirmIds, setConfirmIds] = useState({});
   const [unconfirmIds, setUnconfirmIds] = useState({});
   const [requesting, setRequesting] = useState(false);


   const fetchData = useCallback(async () =>
   {

      //setRequesting(true);

      const data = await fetcher.get(`/api/pianificazione/contratti/${idContratto}/servizi/${idServizio}/`);

      if (fetcher.error)
      {
         setRequesting(false);
         return;
      }

      if (fetcher.response.ok)
      {
         setServizio(data);
         const d = data?.giorni?.find((g) => parseInt(g.id) === parseInt(idGiorno));
         if (d)
         {
            setGiorno(d);
            setTurni(idTurno ? d.turni.filter((t) => parseInt(t.id) === parseInt(idTurno)) : d.turni);
            const t = d.turni.find((t) => parseInt(t.id) === parseInt(idTurno));
            if (t)
            {
               setTurno(t);
            }
         }
         setRequesting(false);
      }

   }, [fetcher, idContratto, idServizio, idGiorno, idTurno])

   useEffect(() =>
   {

      fetchData();

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [idContratto, idServizio, idTurno, idGiorno]);

   useEffect(() =>
   {
      // Annulla le chiamate debounce in sospeso quando il componente viene smontato
      return () =>
      {
         forceReload.cancel();
      };
   }, [forceReload]);

   const unassignOperatore = useCallback(async (id) =>
   {
      setUnassigningIds(prev => ({ ...prev, [id]: true }));
      forceReload();

      const container = {
         data: {
            operatore: { id },
            add: false
         }
      };

      try
      {
         await fetcher.request.post(`/api/pianificazione/contratti/${idContratto}/servizi/${idServizio}/giorni/${idGiorno}/turni/${idTurno}/operatori/`, container);
      } catch (error)
      {
         console.error("Errore durante la de-assegnazione dell'operatore:", error);
         // In caso di errore, si potrebbe voler mostrare una notifica all'utente
      } finally
      {
         setUnassigningIds(prev =>
         {
            const newState = { ...prev };
            delete newState[id];
            return newState;
         });
      }
   }, [idContratto, idServizio, idGiorno, idTurno, forceReload, fetcher])

   const assignOperatore = useCallback(async (id) =>
   {
      setAssigningIds(prev => ({ ...prev, [id]: true }));
      forceReload();

      const container = {
         data: {
            operatore: { id },
            add: true
         }
      };

      try
      {
         await fetcher.request.post(`/api/pianificazione/contratti/${idContratto}/servizi/${idServizio}/giorni/${idGiorno}/turni/${idTurno}/operatori/`, container);
      } catch (error)
      {
         console.error("Errore durante l'assegnazione dell'operatore:", error);
      } finally
      {
         setAssigningIds(prev =>
         {
            const newState = { ...prev };
            delete newState[id];
            return newState;
         });
      }
   }, [idContratto, idServizio, idGiorno, idTurno, forceReload, fetcher]);

   const confirmOperatore = useCallback(async (id) =>
   {
      setConfirmIds({ ...confirmIds, [id]: true });
      forceReload();

      const container = {
         data: {
            operatore: { id },
            add: true
         }
      };

      const confirm = await fetcher.request.post(`/api/pianificazione/contratti/${idContratto}/servizi/${idServizio}/giorni/${idGiorno}/turni/${idTurno}/operatori-conf/`, container);

      if (!fetcher.response.ok)
      {
         console.error("Errore durante la conferma dell'operatore:", fetcher.error);
      }
      setConfirmIds(prev =>
      {
         const newState = { ...prev };
         delete newState[id];
         return newState;
      });
   }, [confirmIds, forceReload, idContratto, idGiorno, idServizio, idTurno, fetcher])

   const unconfirmOperatore = useCallback(async (id) =>
   {
      setUnconfirmIds({ ...unconfirmIds, [id]: true });
      forceReload();

      const container = {
         data: {
            operatore: { id }
         },
      };

      const unconfirm = await fetcher.request.post(`/api/pianificazione/contratti/${idContratto}/servizi/${idServizio}/giorni/${idGiorno}/turni/${idTurno}/operatori-conf/`, container);

      if (!fetcher.response.ok)
      {
         console.error("Errore durante la rimozione della conferma dell'operatore:", fetcher.error);
      }
      setUnconfirmIds(prev =>
      {
         const newState = { ...prev };
         delete newState[id];
         return newState;
      });
   }, [forceReload, idContratto, idGiorno, idServizio, idTurno, unconfirmIds, fetcher])

   return (
      <servizioContext.Provider value={
         {
            //metodi
            unassignOperatore,
            assignOperatore,
            confirmOperatore,
            unconfirmOperatore,
            assigningIds,
            setAssigningIds,
            unassigningIds,
            setUnassigningIds,
            confirmIds,
            setConfirmIds,
            unconfirmIds,
            setUnconfirmIds,
            requesting,
            servizio,
            giorno,
            turni,
            turno,
            ...otherProps
         }
      }>
         {children}
      </servizioContext.Provider>
   );
}

ServizioCore.propTypes = {
   idContratto: oneOfType([
      PropTypes.string,
      PropTypes.number
   ]),
   idServizio: oneOfType([
      PropTypes.string,
      PropTypes.number
   ]),
   idGiorno: oneOfType([
      PropTypes.string,
      PropTypes.number
   ]),
   idTurno: oneOfType([
      PropTypes.string,
      PropTypes.number
   ]),
   otherProps: PropTypes.object
};

ServizioCore.propTypes = {
   children: PropTypes.node,
};